// src/encounter/services/use-assessment.ts
// Load the encounter Assessment & Plan (PROD-1297).
//   GET /api/Patients/GetAssessmentByPatientId?PatientId=&EncounterId= → PatientAssessment[]
// The endpoint returns a LIST; an encounter has at most one assessment so we take the
// FIRST element (PatientAssessment | null). A 404 (ApiError 404) resolves to null —
// mirrors use-sign-lock.ts's try/catch. Query key matches use-save-assessment.ts's
// invalidation (['assessment', subject, encounter]) so a save re-reads here. api-client
// injects org/user headers.
import { useQuery } from '@tanstack/react-query';
import { get, ApiError } from '@/shared/api/api-client';
import type { PatientAssessment } from '@/types/assessment-api';

/**
 * Read the encounter's saved assessment (list → first; 404 → null).
 * Enabled only when both ids are present; query key matches the save hook's invalidation.
 */
export function useEncounterAssessment(
  subjectId: string | null | undefined,
  encounterId: string | null | undefined,
) {
  return useQuery<PatientAssessment | null>({
    queryKey: ['assessment', subjectId, encounterId],
    enabled: !!subjectId && !!encounterId,
    queryFn: async ({ signal }) => {
      // Narrow (not cast) — `enabled` already guards, this keeps the URL from ever
      // being built with "undefined" segments.
      if (!subjectId || !encounterId) return null;
      try {
        const list = await get<PatientAssessment[]>(
          `/api/Patients/GetAssessmentByPatientId?PatientId=${encodeURIComponent(
            subjectId,
          )}&EncounterId=${encodeURIComponent(encounterId)}`,
          { signal },
        );
        return list?.[0] ?? null;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}
